import { oublierAvis } from './avis'
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './supabase'
import type { Profil } from './types'

interface ContexteAuth {
  session: Session | null
  profil: Profil | null
  chargement: boolean
  /** Vrai quand le profil n'a pas pu être relu (réseau) : on affiche le dernier connu. */
  profilIncertain: boolean
  relireProfil: () => void
  deconnexion: () => Promise<void>
}

const Contexte = createContext<ContexteAuth>({
  session: null,
  profil: null,
  chargement: true,
  profilIncertain: false,
  relireProfil: () => {},
  deconnexion: async () => {}
})

const CLE_PROFIL = 'chantiers-larente:profil'

function profilGarde(id: string): Profil | null {
  try {
    const p = JSON.parse(localStorage.getItem(CLE_PROFIL) ?? 'null') as Profil | null
    return p && p.id === id ? p : null
  } catch {
    return null
  }
}

function garderProfil(p: Profil | null) {
  try {
    if (p) localStorage.setItem(CLE_PROFIL, JSON.stringify(p))
    else localStorage.removeItem(CLE_PROFIL)
  } catch {
    /* stockage indisponible */
  }
}

const attendre = (ms: number) => new Promise((r) => setTimeout(r, ms))

export function FournisseurAuth({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profil, setProfil] = useState<Profil | null>(null)
  const [chargement, setChargement] = useState(true)
  const [profilIncertain, setProfilIncertain] = useState(false)
  const [essai, setEssai] = useState(0)

  useEffect(() => {
    let vivant = true

    supabase.auth.getSession().then(({ data }) => {
      if (!vivant) return
      setSession(data.session)
      if (!data.session) setChargement(false)
    })

    const { data: ecoute } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s)
      if (!s) {
        setProfil(null)
        setChargement(false)
      }
    })

    return () => {
      vivant = false
      ecoute.subscription.unsubscribe()
    }
  }, [])

  // Le profil est relu seulement quand l'utilisateur change. Supabase
  // renouvelle la session (nouvel objet) au retour sur l'onglet du
  // navigateur et toutes les heures : sans cette garde, l'écran
  // « Chargement… » remplaçait l'application et on perdait la page
  // consultée (retour à l'onglet Info du chantier).
  const utilisateurId = session?.user.id ?? null
  useEffect(() => {
    if (!utilisateurId) return
    let vivant = true
    // Le dernier profil connu sert tout de suite (et hors ligne) ; avant, un
    // échec de lecture (réseau faible) faisait passer un admin pour un simple
    // utilisateur jusqu'au rechargement.
    const garde = profilGarde(utilisateurId)
    if (garde) {
      setProfil(garde)
      setChargement(false)
    } else {
      setChargement(true)
    }
    void (async () => {
      for (let i = 0; i < 4 && vivant; i++) {
        const { data, error } = await supabase
          .from('profils')
          .select('id, nom, courriel, role, actif')
          .eq('id', utilisateurId)
          .maybeSingle()
        if (!vivant) return
        if (!error) {
          // Pas de ligne lisible = profil absent ou compte désactivé (un compte
          // désactivé ne peut pas lire les profils) : on le traite comme désactivé.
          const p = (data as Profil | null) ?? (garde ? { ...garde, actif: false } : null)
          setProfil(p)
          garderProfil(p)
          setProfilIncertain(false)
          setChargement(false)
          return
        }
        await attendre(1500 * (i + 1))
      }
      if (!vivant) return
      setProfilIncertain(true)
      setChargement(false)
    })()
    return () => {
      vivant = false
    }
  }, [utilisateurId, essai])

  return (
    <Contexte.Provider
      value={{
        session,
        profil,
        chargement,
        profilIncertain,
        relireProfil: () => setEssai((n) => n + 1),
        deconnexion: async () => {
          oublierAvis()
          garderProfil(null)
          await supabase.auth.signOut()
        }
      }}
    >
      {children}
    </Contexte.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): ContexteAuth {
  return useContext(Contexte)
}

/** Droits d'écriture : chef d'équipe ou administrateur, sur un chantier non archivé. */
export function peutEcrire(profil: Profil | null, statutChantier: string | undefined): boolean {
  if (!profil || !profil.actif) return false
  if (statutChantier === 'termine') return false
  return profil.role === 'chef' || profil.role === 'admin' || profil.role === 'superadmin'
}

/** Administrateur ou superadmin (le superadmin a tous les droits d'un admin). */
export function estAdmin(profil: Profil | null): boolean {
  return profil?.actif === true && (profil.role === 'admin' || profil.role === 'superadmin')
}

/** Superadmin : rôles, comptes, suppression définitive, journal d'audit. */
export function estSuperadmin(profil: Profil | null): boolean {
  return profil?.actif === true && profil.role === 'superadmin'
}
